import React from "react";
import {
  Phone, MessageSquare, Activity, Brain, ListChecks,
  Headphones, Search, Sparkles, MicVocal,
} from "lucide-react";
import RichBlogLayout from "../../components/RichBlogLayout";
import {
  Prose, Lead, PullQuote, MetricsRow, BeforeAfter, ProcessFlow,
  ParallelGrid, FeatureCallout,
  LiveFeed, FlowDiagram, SectionDivider,
} from "../../components/blog/BlogGraphics";

const BlogAiNativeCallIntelligence = () => (
  <RichBlogLayout
    category="Practice Intelligence"
    eyebrow="Call Intelligence"
    title="Calls as Structured Data: Why AI-Native Voice Beats a Library of Recordings"
    subtitle="Every call — inbound or outbound, AI-handled or human — auto-classified, sentiment-tagged, and rendered as action items the moment it ends."
    author="Asha Team"
    date="May 2, 2026"
    readTime="8 min read"
  >
    <Lead>
      A typical practice runs 60 to 120 phone calls a day. By 6:00 PM that's
      thousands of minutes of voice that, in a legacy VOIP stack, become
      opaque audio files in a portal nobody opens. In an AI-native
      communication layer, those same calls become structured rows the team
      can actually act on — that is the entire point of recording them in
      the first place.
    </Lead>

    <Prose>
      <p>
        Most dental practices today record their phone calls. Walk into a
        front office and ask "do you record calls?" and the answer is almost
        always yes. Walk into the same front office and ask "when's the last
        time you actually listened to one?" and the answer is almost always
        a long pause. The recording exists. Somewhere. In a portal nobody
        opens. With no search, no summary, no link to the patient.
      </p>
      <p>
        That gap — between recording calls and using calls — is the gap an
        AI-native communication layer closes. Not by storing them better.
        By treating them, from the second they end, as structured data with
        the same first-class properties as a chart note or a billing line.
      </p>
    </Prose>

    <MetricsRow
      items={[
        { value: 100, suffix: "%", label: "Of calls auto-transcribed" },
        { value: 4, label: "Auto-classifications · billing, scheduling, clinical, triage" },
        { value: 0, label: "After-call notes to type" },
        { value: 60, prefix: "~", label: "Seconds from hangup to action item" },
      ]}
    />

    <Prose>
      <h2>Why legacy VOIP stops at the audio file</h2>
      <p>
        Tools like Weave or Mango weren't built wrong — they were built
        before structured AI was an option. Their architecture is "capture
        the audio, store it, expose it as a recording." That is the entire
        product. There is no understanding layer because, until recently,
        there couldn't be one. A faithful recording with no structure is a
        haystack.
      </p>
      <p>
        And so practices end up paying for a recording library nobody uses.
        Calls are still triaged by sticky note, action items still get
        forgotten, and the institutional value of every conversation
        evaporates roughly two minutes after the line goes dead.
      </p>
    </Prose>

    <BeforeAfter
      beforeTitle="Legacy VOIP (Weave / Mango)"
      afterTitle="AI-native call intelligence"
      beforeItems={[
        "Recording captured and stored as an audio file",
        "No transcription, no search, no classification",
        "Action items live in a teammate's head — or get forgotten",
        "Call recordings sit in a library nobody listens to",
      ]}
      afterItems={[
        "Every call processed the moment it ends — AI-handled or human",
        "Auto-classified by intent: billing, scheduling, clinical, triage",
        "Sentiment-tagged with a timeline showing how the conversation shifted",
        "Action items extracted and routed to the right teammate automatically",
      ]}
    />

    <Prose>
      <h2>What happens the moment a call ends</h2>
      <p>
        The interesting moment in an AI-native architecture isn't during the
        call. It's the second the line goes dead. That second is when a
        legacy VOIP system saves an audio file and stops working. It is when
        an AI-native system actually starts.
      </p>
    </Prose>

    <FlowDiagram
      title="Post-call processing — runs on every single call"
      nodes={[
        { label: "Call ends", sublabel: "AI or human, in or out", icon: Phone },
        { label: "Diarized transcript", sublabel: "Who said what", icon: Activity },
        { label: "Classified + tagged", sublabel: "Intent + sentiment", icon: Brain },
        { label: "Action items routed", sublabel: "Tasks on the right desk", icon: ListChecks },
      ]}
    />

    <Prose>
      <h2>The five things every call becomes</h2>
      <p>
        Click into any row in the call view and you don't see a media player.
        You see a structured record. The audio is still there for the rare
        case you need it, but it isn't the primary surface anymore. The
        primary surface is the structure.
      </p>
    </Prose>

    <ProcessFlow
      steps={[
        {
          title: "A clean summary at the top",
          body:
            "Three or four sentences that explain what the call was about, what was decided, and what's expected next. Skim-readable in five seconds.",
        },
        {
          title: "A speaker-diarized transcript",
          body:
            "Each line tagged with who said it. Not a wall of text — a readable conversation, searchable across every call this patient has ever had.",
        },
        {
          title: "An intent classification",
          body:
            "Was this billing? Scheduling? Clinical? Triage? The category is assigned automatically and drives downstream routing and analytics.",
        },
        {
          title: "A sentiment timeline",
          body:
            "How tone shifted across the call — neutral start, frustration spike at minute three, resolution by minute six. Visible at a glance.",
        },
        {
          title: "Linked, routed action items",
          body:
            "Every commitment made on the call ('I'll send you a payment plan,' 'we'll book you Friday') becomes a task on the right teammate's queue with the call attached.",
        },
      ]}
    />

    <PullQuote>
      Calls don't just disappear anymore. They become structured, usable data.
    </PullQuote>

    <Prose>
      <h2>What it actually looks like in a morning</h2>
      <p>
        The post-call queue on a real Tuesday morning is not a list of
        recordings. It's a feed of structured outcomes — each one already
        classified, sentiment-tagged, and converted into the next step.
      </p>
    </Prose>

    <LiveFeed
      title="Calls processed · this morning"
      items={[
        { time: "08:14", label: "Inbound · scheduling · neutral", detail: "Maria L. · booked hygiene Tue 9:30 · auto-confirmed", status: "done" },
        { time: "08:31", label: "Inbound · billing · frustrated → resolved", detail: "James K. · payment plan offered · routed to Rachel", status: "done" },
        { time: "08:42", label: "Outbound · clinical follow-up · positive", detail: "Anna P. · post-op check · no action needed", status: "done" },
        { time: "08:55", label: "Inbound · triage · urgent", detail: "David R. · swelling · on-call provider looped in", status: "done" },
        { time: "09:08", label: "Inbound · scheduling · neutral", detail: "Priya S. · family of 3 booked · routed to scheduler", status: "done" },
        { time: "09:21", label: "Inbound · billing · neutral", detail: "Linda M. · statement question · self-resolved on call", status: "done" },
      ]}
    />

    <Prose>
      <h2>The same intelligence, whether AI or human handled it</h2>
      <p>
        A subtle but important property of AI-native call intelligence: it
        does not care who handled the call. When Amy answers a call, the
        post-processing happens. When a human teammate answers a call, the
        same post-processing happens. The structure, the transcript, the
        sentiment, the routed action items — all identical.
      </p>
      <p>
        That uniformity is what makes the data useful at the practice level.
        You can finally ask honest operational questions like "what
        percentage of our inbound billing calls escalated to frustration this
        month" because every call, regardless of who picked it up, was
        scored the same way.
      </p>
    </Prose>

    <ParallelGrid
      title="Everything tied back to the patient — and to each other"
      columns={3}
      items={[
        { title: "Inbound Amy call", subtitle: "Scheduling · neutral", icon: Phone, status: "done" },
        { title: "Outbound Rachel call", subtitle: "Billing · resolved", icon: MicVocal, status: "done" },
        { title: "Human-answered call", subtitle: "Same post-processing", icon: Headphones, status: "done" },
        { title: "Auto-classified", subtitle: "Billing / sched / clinical / triage", icon: Brain, status: "active" },
        { title: "Sentiment timeline", subtitle: "Tone shifts visualized", icon: Activity, status: "active" },
        { title: "Tasks created", subtitle: "Routed with call attached", icon: ListChecks, status: "active" },
      ]}
    />

    <Prose>
      <h2>Search becomes the operating mode</h2>
      <p>
        Once every call is transcribed, classified, and tied to the patient
        record, the entire conversational history of the practice becomes
        searchable. "Did anyone tell Maria the crown was a buildup?" becomes
        a one-second answer instead of an unanswerable question. A pattern
        of patients mentioning the same insurance issue becomes visible the
        first week instead of the first quarter.
      </p>
      <p>
        That search-as-default mode is not possible in a recording library.
        It only emerges when calls become structured data from the moment
        they end.
      </p>
    </Prose>

    <FeatureCallout
      stat="Every call → structured row"
      label="Not an audio file. A summary, a transcript, a sentiment line, and a routed task."
      description="The recording is still there for the rare deep dive — but it isn't where the work happens anymore."
      icon={Sparkles}
    />

    <Prose>
      <h2>What disappears from the front desk's day</h2>
      <p>
        After-call note-taking disappears. Sticky-note triage disappears.
        The "I think she said something about Friday" guesswork disappears.
        The team stops experiencing calls as a one-shot event and starts
        experiencing them as a thread that continues into the chart, the
        scheduler's queue, and the biller's task list — automatically, every
        time, without anyone remembering to do it.
      </p>
      <p>
        From the operator's seat, this is the biggest underrated decision
        most practices have not yet made. Every other AI workflow — billing,
        scheduling, recall, intake — gets compounding value when calls
        become structured data, because every downstream workflow can read
        from the call record. Replacing a legacy VOIP isn't a phone change.
        It's the foundation of everything else.
      </p>
    </Prose>

    <SectionDivider label="Bottom line" />

    <Prose>
      <p>
        A library of recordings nobody listens to is not call intelligence.
        AI-native voice turns every conversation into a summary, a transcript,
        a sentiment line, and a routed task — the moment it ends. That is
        what unlocks every other workflow downstream of the call.
      </p>
    </Prose>
  </RichBlogLayout>
);

export default BlogAiNativeCallIntelligence;
